import { useTheme } from "@app/ui"
import type { ThemePreference } from "@app/ui"
import type { ReactNode } from "react"

/**
 * Three-way (light / dark / system) theme control for the dashboard "LIVE" row.
 *
 * Mirrors the themeSwitch slot in the design export: a small segmented control
 * exposing all three options at once. `system` tracks the OS `prefers-color-scheme`
 * live; the product default is `light` (see `@app/ui` `ThemeProvider`). The active
 * segment reflects the stored *preference*, not the resolved theme — so `system`
 * stays highlighted even while it resolves to light or dark.
 */

const ICON_SUN = (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<circle cx="12" cy="12" r="4" />
		<path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
	</svg>
)

const ICON_MOON = (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M21 12.8A8.5 8.5 0 1 1 11.2 3 6.6 6.6 0 0 0 21 12.8z" />
	</svg>
)

const ICON_SYSTEM = (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="2" y="4" width="20" height="13" rx="2" />
		<path d="M8 21h8M12 17v4" />
	</svg>
)

const OPTIONS: ReadonlyArray<{ value: ThemePreference; label: string; icon: ReactNode }> = [
	{ value: "light", label: "Light", icon: ICON_SUN },
	{ value: "dark", label: "Dark", icon: ICON_MOON },
	{ value: "system", label: "System", icon: ICON_SYSTEM },
]

export function ThemeToggle() {
	const { preference, setTheme } = useTheme()
	return (
		<fieldset className="gm-theme-seg">
			<legend className="gm-theme-seg__legend">Theme</legend>
			{OPTIONS.map((opt) => (
				<button
					key={opt.value}
					type="button"
					className="gm-theme-seg__btn"
					aria-pressed={preference === opt.value}
					aria-label={`${opt.label} theme`}
					title={`${opt.label} theme`}
					onClick={() => setTheme(opt.value)}
				>
					{opt.icon}
				</button>
			))}
		</fieldset>
	)
}
